import { FC, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { defineMessages, FormattedMessage, useIntl } from 'dibs-react-intl';
import classnames from 'classnames';

import {
    recognizedTitle,
    recognizedDescription,
    goldSellerTitle,
    goldSellerDescription,
    platinumSellerTitle,
    diamondSellerTitle,
    tieredSellerDescription,
    associationsTitle,
    associationsDescription,
} from '../messages/pdpDistinctionsMessages';
import RecognizedIcon from 'dibs-icons/exports/legacy/RecognizedRibbon';
import TopSellerIcon from 'dibs-icons/exports/legacy/Diamond';
import DiamondFilled from 'dibs-icons/exports/legacy/DiamondFilled';
import AssociationsIcon from 'dibs-icons/exports/legacy/LeafCrown';
import { PdpRespSidebarSellerInfoTooltip } from './PdpRespSidebarSellerInfoTooltip';
import { Badge } from 'dibs-elements/exports/Badge';

import { useSellerRating } from '../../shared/hooks/useSellerRating';
import { getIsNewSeller } from '../../utils/sellerHelpers';

import { PdpRespSidebarSellerInfoBadges_seller$key } from './__generated__/PdpRespSidebarSellerInfoBadges_seller.graphql';

import styles from './PdpRespSidebarSellerInfoBadges.scss';

const sellerFragment = graphql`
    fragment PdpRespSidebarSellerInfoBadges_seller on Seller {
        monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
        isDistinguished
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        sellerTiers {
            isGoldTier
            isPlatinumTier
            sellerTierDataNotFound
        }
        tradeAssociations {
            serviceId
        }
        ...useSellerRating_seller
    }
`;

const copy = defineMessages({
    sellerTierLabel: {
        id: 'PdpRespSidebarSellerInfoBadges.sellerTierLabel',
        defaultMessage: 'Seller Tier',
    },
    sellerAssociationsLabel: {
        id: 'PdpRespSidebarSellerInfoBadges.sellerAssociations',
        defaultMessage: 'Seller Associations',
    },
    recognizedLabel: {
        id: 'PdpRespSidebarSellerInfoBadges.recognizedLabel',
        defaultMessage: 'Recognized',
    },
});

export const PdpRespSidebarSellerInfoBadges: FC<{
    seller: PdpRespSidebarSellerInfoBadges_seller$key;
}> = ({ seller: sellerRef }) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const {
        monthsSinceStartDate,
        sellerTiers,
        isRewardsTier2,
        isDistinguished,
        tradeAssociations,
    } = seller;

    const intl = useIntl();
    const rating = useSellerRating({ seller });
    const isNewSeller = getIsNewSeller(monthsSinceStartDate);

    const { sellerTierDataNotFound, isGoldTier, isPlatinumTier } = sellerTiers || {};
    const sellerTierDataFound = !sellerTierDataNotFound;
    const isGoldSeller = sellerTierDataFound && isGoldTier;
    const isPlatinumSeller = sellerTierDataFound && isPlatinumTier;
    const isTopSeller = isGoldSeller || isPlatinumSeller || isRewardsTier2;

    const badges: ReactNode[] = [];

    if (rating === 0 && isNewSeller) {
        return (
            <Badge type="pill" size="small-heavy">
                <FormattedMessage
                    id="PdpRespSidebarSellerInfoBadges.newTo1stDibs"
                    defaultMessage="New to 1stDibs"
                />
            </Badge>
        );
    }
    if (isTopSeller) {
        let title = null;
        let description = tieredSellerDescription;
        let className = '';
        let Icon = TopSellerIcon;
        if (isRewardsTier2) {
            title = diamondSellerTitle;
            Icon = DiamondFilled;
        } else if (isGoldSeller) {
            title = goldSellerTitle;
            description = goldSellerDescription;
            className = styles.goldDiamond;
        } else if (isPlatinumSeller) {
            title = platinumSellerTitle;
            className = styles.platinumDiamond;
        }
        badges.push(
            <PdpRespSidebarSellerInfoTooltip
                key="topTier"
                ariaLabel={intl.formatMessage(copy.sellerTierLabel)}
                title={title}
                description={description}
                dataTn="pdp-sidebar-seller-top-tier"
                wrapperClass={styles.wrapper}
                linkClass={styles.tooltipLink}
            >
                <Icon className={classnames(styles.icon, className)} />
            </PdpRespSidebarSellerInfoTooltip>
        );
    }
    if (isDistinguished) {
        badges.push(
            <PdpRespSidebarSellerInfoTooltip
                key="recognized"
                ariaLabel={intl.formatMessage(copy.sellerAssociationsLabel)}
                title={recognizedTitle}
                description={recognizedDescription}
                dataTn="pdp-sidebar-seller-recognized"
                wrapperClass={styles.wrapper}
                linkClass={styles.tooltipLink}
            >
                <RecognizedIcon className={styles.icon} />
            </PdpRespSidebarSellerInfoTooltip>
        );
    }
    if ((tradeAssociations || []).length) {
        badges.push(
            <PdpRespSidebarSellerInfoTooltip
                key="associations"
                ariaLabel={intl.formatMessage(copy.recognizedLabel)}
                title={associationsTitle}
                description={associationsDescription}
                dataTn="pdp-sidebar-seller-associations"
                wrapperClass={styles.wrapper}
                linkClass={styles.tooltipLink}
            >
                <AssociationsIcon className={styles.icon} />
            </PdpRespSidebarSellerInfoTooltip>
        );
    }

    return <>{badges}</>;
};
